const { Criteria } = Shopware.Data;

/**
 * Regeln steuern, was bei einem Begriff passiert:
 *   pin      - Artikel auf Platz N der ersten Seite
 *   boost    - Artikel bzw. Treffer höher gewichten (Faktor > 1)
 *   bury     - niedriger gewichten (Faktor < 1)
 *   redirect - Begriff führt direkt auf eine Seite, ohne Trefferliste
 */
export default {
    template: `
<sw-page class="riccardo-search-rules">
    <template #smart-bar-header><h2>Suche – Regeln</h2></template>
    <template #smart-bar-actions>
        <sw-button :router-link="{ name: 'riccardo.search.terms' }">Auswertung</sw-button>
        <sw-button variant="primary" @click="neu()">Regel anlegen</sw-button>
    </template>
    <template #content>
        <div style="padding: 24px;">
            <sw-card :is-loading="isLoading" title="Regeln">
                <p style="color:#798490">
                    Regeln greifen auf den normalisierten Begriff. „Genaue Übereinstimmung“ aus bedeutet:
                    die Regel greift, sobald der Begriff enthalten ist.
                </p>
                <sw-entity-listing v-if="items" :items="items" :repository="repository" :columns="columns"
                                   :show-selection="true" :allow-inline-edit="false" :full-page="false"
                                   detail-route="">
                    <template #column-type="{ item }">
                        <sw-label :variant="farbe(item.type)" size="small">{{ typLabel(item.type) }}</sw-label>
                    </template>
                    <template #column-ziel="{ item }">
                        <span v-if="item.type === 'redirect'">{{ item.targetUrl }}</span>
                        <sw-product-variant-info v-else-if="item.product" :variations="item.product.variation">
                            {{ item.product.translated.name || item.product.productNumber }}
                        </sw-product-variant-info>
                        <span v-else style="color:#798490">alle Treffer</span>
                    </template>
                    <template #column-wert="{ item }">
                        <span v-if="item.type === 'pin'">Platz {{ item.position }}</span>
                        <span v-else-if="item.type === 'boost' || item.type === 'bury'">Faktor {{ item.factor }}</span>
                        <span v-else>–</span>
                    </template>
                    <template #column-active="{ item }">
                        <sw-icon :name="item.active ? 'regular-checkmark-xs' : 'regular-times-xs'"
                                 :color="item.active ? '#37D046' : '#DE294C'" small />
                    </template>
                    <template #more-actions="{ item }">
                        <sw-context-menu-item @click="bearbeiten(item)">Bearbeiten</sw-context-menu-item>
                    </template>
                </sw-entity-listing>
            </sw-card>
        </div>

        <sw-modal v-if="entwurf" :title="entwurf._isNew ? 'Regel anlegen' : 'Regel bearbeiten'"
                  @modal-close="entwurf = null">
            <sw-text-field v-model:value="entwurf.term" label="Suchbegriff" placeholder="elfbar" />
            <sw-single-select v-model:value="entwurf.type" label="Art" :options="typen" />

            <sw-entity-single-select v-if="entwurf.type !== 'redirect'" v-model:value="entwurf.productId"
                                     entity="product" label="Artikel"
                                     help-text="Ohne Artikel wirkt boost/bury auf alle Treffer des Begriffs." />
            <sw-text-field v-if="entwurf.type === 'redirect'" v-model:value="entwurf.targetUrl"
                           label="Zielseite" placeholder="/deals" />

            <sw-number-field v-if="entwurf.type === 'pin'" v-model:value="entwurf.position"
                             label="Platz" :min="1" :step="1" />
            <sw-number-field v-if="entwurf.type === 'boost' || entwurf.type === 'bury'"
                             v-model:value="entwurf.factor" label="Faktor" :step="0.1" number-type="float"
                             :help-text="entwurf.type === 'boost' ? 'Größer als 1, z. B. 2.0' : 'Kleiner als 1, z. B. 0.3'" />

            <sw-switch-field v-model:value="entwurf.exactMatch" label="Genaue Übereinstimmung" />
            <sw-switch-field v-model:value="entwurf.active" label="Aktiv" />
            <sw-datepicker v-model:value="entwurf.validFrom" date-type="datetime" label="Gültig ab" />
            <sw-datepicker v-model:value="entwurf.validUntil" date-type="datetime" label="Gültig bis" />
            <sw-text-field v-model:value="entwurf.comment" label="Notiz" />

            <template #modal-footer>
                <sw-button size="small" @click="entwurf = null">Abbrechen</sw-button>
                <sw-button variant="primary" size="small" :disabled="!gueltig" @click="speichern">Speichern</sw-button>
            </template>
        </sw-modal>
    </template>
</sw-page>`,

    inject: ['repositoryFactory'],

    data() {
        return { items: null, isLoading: false, entwurf: null };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('riccardo_search_rule');
        },
        typen() {
            return [
                { value: 'pin', label: 'Anheften (fester Platz)' },
                { value: 'boost', label: 'Hochstufen' },
                { value: 'bury', label: 'Herabstufen' },
                { value: 'redirect', label: 'Weiterleitung' },
            ];
        },
        columns() {
            return [
                { property: 'term', label: 'Begriff', primary: true },
                { property: 'type', label: 'Art' },
                { property: 'ziel', label: 'Ziel' },
                { property: 'wert', label: 'Wert' },
                { property: 'active', label: 'Aktiv', align: 'center' },
                { property: 'comment', label: 'Notiz' },
            ];
        },
        gueltig() {
            const e = this.entwurf;
            if (!e || !e.term || !e.type) return false;
            if (e.type === 'redirect') return !!e.targetUrl;
            if (e.type === 'pin') return !!e.productId;
            return true;
        },
    },

    created() {
        this.laden();
        if (this.$route.query.term) {
            this.neu(this.$route.query.term);
        }
    },

    methods: {
        laden() {
            this.isLoading = true;
            const criteria = new Criteria(1, 50);
            criteria.addAssociation('product');
            criteria.addSorting(Criteria.sort('term', 'ASC'));
            criteria.addSorting(Criteria.sort('position', 'ASC'));
            // inheritance: Varianten haben keinen eigenen Namen, der kommt vom
            // Hauptartikel. Ohne das Flag blieb die Spalte "Ziel" leer.
            this.repository.search(criteria, { ...Shopware.Context.api, inheritance: true })
                .then((r) => { this.items = r; })
                .finally(() => { this.isLoading = false; });
        },
        neu(term) {
            const e = this.repository.create(Shopware.Context.api);
            e.term = typeof term === 'string' ? term : '';
            e.type = 'pin';
            e.position = 1;
            e.factor = 2;
            e.exactMatch = true;
            e.active = true;
            this.entwurf = e;
        },
        bearbeiten(item) {
            this.repository.get(item.id, Shopware.Context.api).then((e) => { this.entwurf = e; });
        },
        speichern() {
            // Nur das Feld füllen, das zur Art gehört - sonst steht z. B. bei einer
            // Weiterleitung noch ein Artikel drin und verwirrt beim nächsten Öffnen.
            const e = this.entwurf;
            if (e.type === 'redirect') { e.productId = null; }
            if (e.type !== 'redirect') { e.targetUrl = null; }

            // Ohne die Version bleibt product_version_id leer und die Verknüpfung
            // zum Artikel lädt nicht mehr - in der Liste stand dann "alle Treffer",
            // obwohl ein Artikel gewählt war.
            e.productVersionId = e.productId ? Shopware.Context.api.liveVersionId : null;
            if (e.type !== 'pin') { e.position = null; }
            if (e.type !== 'boost' && e.type !== 'bury') { e.factor = null; }

            this.repository.save(e, Shopware.Context.api).then(() => {
                this.entwurf = null;
                this.laden();
                Shopware.Store.get('notification').createNotification({
                    variant: 'success',
                    message: 'Regel gespeichert. Wirkt sofort, der Zwischenspeicher hält 5 Minuten.',
                });
            });
        },
        typLabel(type) {
            return (this.typen.find((t) => t.value === type) || {}).label || type;
        },
        farbe(type) {
            return { pin: 'info', boost: 'success', bury: 'warning', redirect: 'primary' }[type] || 'neutral';
        },
    },
};
